/**
 * Where a connector meets a seat box. Stored as a side plus a fraction along
 * that side rather than a coordinate, so the attachment survives the box moving
 * or changing height.
 */
export type EdgeAttachmentSide = 'top' | 'right' | 'bottom' | 'left';

export type EdgeAttachment = {
  side: EdgeAttachmentSide;
  /** 0..1 along the side, left-to-right or top-to-bottom. */
  offset: number;
};

export type RosterEdgeAttachments = {
  source?: EdgeAttachment;
  target?: EdgeAttachment;
};

export type FlowPoint = { x: number; y: number };

export type NodeSize = { width: number; height: number };

export const DEFAULT_SOURCE_ATTACH: EdgeAttachment = {
  side: 'bottom',
  offset: 0.5,
};

export const DEFAULT_TARGET_ATTACH: EdgeAttachment = {
  side: 'top',
  offset: 0.5,
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

export function attachmentToPoint(
  nodeX: number,
  nodeY: number,
  attach: EdgeAttachment,
  size: NodeSize
): FlowPoint {
  const offset = clamp(attach.offset, 0, 1);
  switch (attach.side) {
    case 'top':
      return { x: nodeX + offset * size.width, y: nodeY };
    case 'bottom':
      return { x: nodeX + offset * size.width, y: nodeY + size.height };
    case 'left':
      return { x: nodeX, y: nodeY + offset * size.height };
    case 'right':
    default:
      return { x: nodeX + size.width, y: nodeY + offset * size.height };
  }
}

/**
 * Nearest point on the box outline to an arbitrary pointer position, which is
 * what turns a free drag of a connector end into a legal attachment.
 */
export function projectToNodePerimeter(
  nodeX: number,
  nodeY: number,
  pointerX: number,
  pointerY: number,
  size: NodeSize
): { point: FlowPoint; attach: EdgeAttachment } {
  const left = nodeX;
  const right = nodeX + size.width;
  const top = nodeY;
  const bottom = nodeY + size.height;

  const alongX = clamp(pointerX, left, right);
  const alongY = clamp(pointerY, top, bottom);

  const candidates: { attach: EdgeAttachment; point: FlowPoint }[] = [
    {
      attach: { side: 'top', offset: (alongX - left) / size.width },
      point: { x: alongX, y: top },
    },
    {
      attach: { side: 'bottom', offset: (alongX - left) / size.width },
      point: { x: alongX, y: bottom },
    },
    {
      attach: { side: 'left', offset: (alongY - top) / size.height },
      point: { x: left, y: alongY },
    },
    {
      attach: { side: 'right', offset: (alongY - top) / size.height },
      point: { x: right, y: alongY },
    },
  ];

  let best = candidates[0];
  let bestDistance = Infinity;
  for (const candidate of candidates) {
    const dx = candidate.point.x - pointerX;
    const dy = candidate.point.y - pointerY;
    const distance = dx * dx + dy * dy;
    if (distance < bestDistance) {
      bestDistance = distance;
      best = candidate;
    }
  }

  return best;
}
